import { Link } from 'react-router-dom';
import { 
  ArrowRight, Users, Heart, Briefcase, GraduationCap, 
  DollarSign, BookOpen, Scale, Monitor, Wifi, Globe,
  CheckCircle, Award, Building2
} from 'lucide-react';
import { SEO, pageSEO } from '../components/SEO';

const supportServices = [
  { icon: Heart, name: 'Counseling Services', description: 'Personal and academic counseling support for student well-being and success.' },
  { icon: Users, name: 'Disability Services', description: 'Accommodations and support for students with disabilities to ensure equal access.' },
  { icon: Heart, name: 'Health Services', description: 'On-campus health resources and referrals for student healthcare needs.' },
  { icon: Briefcase, name: 'Career Centers', description: 'Career guidance, job placement assistance, and professional development resources.' },
  { icon: DollarSign, name: 'Financial Aid', description: 'Scholarships, payment plans, and financial assistance for qualifying students.' },
  { icon: BookOpen, name: 'Academic Advising', description: 'Personalized guidance on course selection, degree planning, and academic goals.' },
  { icon: Scale, name: 'Academic Appeals & Human Rights Office', description: 'Fair resolution of academic disputes and protection of student rights.' },
];

const onlineFeatures = [
  { title: 'World-Class Programs', description: 'High-quality, affordable education regardless of your location.' },
  { title: 'International Recognition', description: 'Degrees earned through CIU are recognized worldwide, enabling graduates to work anywhere.' },
  { title: 'Digital Classroom', description: 'All the tools required to support interactive, flexible, and impactful education.' },
  { title: 'Learning Management System', description: 'Modern LMS delivering education accessible regardless of location or schedule.' },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.services} url="/services" />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0D4A2E] pattern-overlay">
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            Campus <span className="text-[#C5A028]">Services</span>
          </h1>
          <p className="text-white/70 text-xl max-w-2xl">
            CEPRES International University supports your career ambitions by aligning 
            your goals with practical education, accredited programs, and real-world relevance.
          </p>
        </div>
      </section>

      {/* Key Message */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0D4A2E] mb-6">
                Your Success is Our <span className="text-[#C5A028]">Priority</span>
              </h2>
              <p className="text-[#666666] mb-4 leading-relaxed">
                Our academic offerings are fully accredited by the National Commission on Higher 
                Education (NCHE) of the Republic of Liberia.
              </p>
              <p className="text-[#666666] mb-4 leading-relaxed">
                Our hybrid and cost-effective learning model gives students the flexibility to 
                graduate debt-free, equipped with the skills and knowledge required to become 
                industry leaders.
              </p>
              <p className="text-[#666666] leading-relaxed">
                CIU provides a supportive learning environment, modern competencies, and 
                qualified instructors dedicated to turning your aspirations into reality.
              </p>
            </div>
            <div className="bg-[#F5F5F5] p-8">
              <div className="flex items-center gap-4 mb-6">
                <Award className="w-12 h-12 text-[#C5A028]" />
                <div>
                  <h3 className="text-xl font-bold text-[#0D4A2E]">NCHE Accredited</h3>
                  <p className="text-[#666666] text-sm">Fully recognized by Liberia's national accreditation body</p>
                </div>
              </div>
              <div className="flex items-center gap-4 mb-6">
                <Globe className="w-12 h-12 text-[#C5A028]" />
                <div>
                  <h3 className="text-xl font-bold text-[#0D4A2E]">International Recognition</h3>
                  <p className="text-[#666666] text-sm">Member of IAMA with global partnerships</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Building2 className="w-12 h-12 text-[#C5A028]" />
                <div>
                  <h3 className="text-xl font-bold text-[#0D4A2E]">Hybrid Learning</h3>
                  <p className="text-[#666666] text-sm">Flexible online and in-person options</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Student Support Services */}
      <section data-testid="support-services" className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D4A2E] mb-4">Student Support Services</h2>
          <p className="text-[#666666] mb-12 max-w-2xl">
            Comprehensive support to ensure your success throughout your academic journey.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {supportServices.map((service, index) => (
              <div 
                key={index}
                className="bg-white p-6 border-l-4 border-[#C5A028] hover:shadow-lg transition-shadow duration-300"
              >
                <service.icon className="w-10 h-10 text-[#0D4A2E] mb-4" />
                <h3 className="text-lg font-bold text-[#0D4A2E] mb-2">{service.name}</h3>
                <p className="text-[#666666] text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Online Services */}
      <section data-testid="online-services" className="py-20 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Our <span className="text-[#C5A028]">Online</span> Services
              </h2>
              <p className="text-white/80 mb-6 leading-relaxed">
                CIU offers world-class online programs that deliver high-quality, affordable 
                education regardless of your location.
              </p>
              <p className="text-white/80 mb-6 leading-relaxed">
                Degrees earned through CIU are internationally recognized, enabling graduates 
                to work anywhere in the world.
              </p>
              <p className="text-white/80 leading-relaxed">
                In today's digital age, the Digital Classroom provides the most effective 
                learning experience—offering all the tools required to support interactive, 
                flexible, and impactful education.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {onlineFeatures.map((feature, index) => (
                <div 
                  key={index}
                  className="bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors duration-300"
                >
                  <Monitor className="w-8 h-8 text-[#C5A028] mb-3" />
                  <h3 className="font-bold mb-2">{feature.title}</h3>
                  <p className="text-white/60 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blended Learning */}
      <section data-testid="blended-learning" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Wifi className="w-16 h-16 text-[#C5A028] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-[#0D4A2E] mb-6">
              Blended Learning <span className="text-[#C5A028]">Services</span>
            </h2>
            <p className="text-[#666666] text-lg mb-6 leading-relaxed">
              Our hybrid learning environment combines the strengths of online education 
              with traditional classroom instruction.
            </p>
            <p className="text-[#666666] text-lg leading-relaxed">
              This approach allows students to enjoy the flexibility of digital learning 
              while benefiting from in-person academic engagement—delivering high-quality 
              education at an affordable cost.
            </p>
            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <Link 
                to="/programs"
                className="inline-flex items-center gap-2 bg-[#0D4A2E] text-white px-8 py-4 font-bold hover:bg-[#082D1C] transition-colors duration-200"
              >
                <ArrowRight className="w-5 h-5" />
                VIEW PROGRAMS
              </Link>
              <a 
                href="https://cnte.ciu.edu.lr/apply"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200"
              >
                APPLY NOW
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
